// Run detail (§11 `/runs/:id`): full attempt timeline (immutable run_attempts forensic table),
// response status, error, callback delivery status, and a replay action (creates a manual replay
// run — excluded from the dedup gate, invariant #2).

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Api, ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtDuration, fmtTime } from "../lib/format";
import { Badge, Card, Empty, ErrorBox, Spinner, StatusBadge } from "../components/ui";

export function RunDetailPage() {
  const { id = "" } = useParams();
  const run = useAsync(() => Api.getRun(id), [id]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [replayId, setReplayId] = useState<string | null>(null);

  async function replay() {
    setActionError(null);
    setBusy(true);
    try {
      const r = await Api.replayRun(id);
      setReplayId(r.id);
      run.reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? new Error(err.message) : err instanceof Error ? err : new Error(String(err)),
      );
    } finally {
      setBusy(false);
    }
  }

  if (run.loading && !run.data) return <Spinner />;
  if (run.error) return <ErrorBox error={run.error} />;
  if (!run.data) return null;
  const r = run.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-mono text-xl font-semibold text-white">{r.id}</h1>
          <div className="text-sm text-muted">
            <Link className="text-primary hover:underline" to={`/tasks/${r.taskId}`}>
              {r.taskId}
            </Link>{" "}
            · attempt {r.attempt} · {r.trigger}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn" disabled={busy} onClick={replay}>
            Replay
          </button>
          <Link className="btn" to="/runs">
            Back
          </Link>
        </div>
      </div>

      {actionError && <ErrorBox error={actionError} />}
      {replayId && (
        <div className="rounded border border-ok/40 bg-ok/10 p-2 text-sm text-ok">
          Replay enqueued as{" "}
          <Link className="font-mono underline" to={`/runs/${replayId}`}>
            {replayId}
          </Link>
          .
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Summary">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-muted">Status</dt>
            <dd>
              <StatusBadge status={r.status} />
            </dd>
            <dt className="text-muted">Scheduled slot</dt>
            <dd className="mono text-xs">{fmtTime(r.scheduledSlot)}</dd>
            <dt className="text-muted">Started</dt>
            <dd className="mono text-xs">{fmtTime(r.startedAt)}</dd>
            <dt className="text-muted">Finished</dt>
            <dd className="mono text-xs">{fmtTime(r.finishedAt)}</dd>
            <dt className="text-muted">Duration</dt>
            <dd className="mono text-xs">{fmtDuration(r.durationMs)}</dd>
            <dt className="text-muted">HTTP status</dt>
            <dd className="mono text-xs">{r.responseStatus ?? "—"}</dd>
            {r.groupMemberIndex != null && (
              <>
                <dt className="text-muted">Group member</dt>
                <dd className="mono text-xs">#{r.groupMemberIndex}</dd>
              </>
            )}
          </dl>
        </Card>

        <Card title="Correlation">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-muted">Correlation ID</dt>
            <dd className="mono break-all text-xs">{r.correlationId ?? "—"}</dd>
            <dt className="text-muted">Idempotency key</dt>
            <dd className="mono break-all text-xs">{r.idempotencyKey ?? "—"}</dd>
            <dt className="text-muted">Dedup ID</dt>
            <dd className="mono break-all text-xs">{r.dedupId ?? "—"}</dd>
            <dt className="text-muted">Traceparent</dt>
            <dd className="mono break-all text-xs">{r.traceparent ?? "—"}</dd>
          </dl>
        </Card>
      </div>

      {r.error && (
        <Card title="Error">
          <pre className="overflow-x-auto rounded bg-bg p-3 font-mono text-xs text-danger">{r.error}</pre>
        </Card>
      )}

      {r.result != null && (
        <Card title="Result">
          <pre className="overflow-x-auto rounded bg-bg p-3 font-mono text-xs text-slate-300">
            {JSON.stringify(r.result, null, 2)}
          </pre>
        </Card>
      )}

      <Card title="Attempt timeline">
        {!r.attempts || r.attempts.length === 0 ? (
          <Empty>No recorded attempts.</Empty>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">Attempt</th>
                <th className="th">Status</th>
                <th className="th">HTTP</th>
                <th className="th">Duration</th>
                <th className="th">Callback</th>
                <th className="th">Started</th>
                <th className="th">Error</th>
              </tr>
            </thead>
            <tbody>
              {r.attempts.map((a) => (
                <tr key={a.id}>
                  <td className="td mono text-xs">{a.attempt}</td>
                  <td className="td">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="td mono text-xs">{a.responseStatus ?? "—"}</td>
                  <td className="td mono text-xs">{fmtDuration(a.durationMs)}</td>
                  <td className="td">
                    {a.callbackStatus == null ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <Badge tone={a.callbackStatus < 400 ? "ok" : "danger"}>{a.callbackStatus}</Badge>
                    )}
                  </td>
                  <td className="td mono text-xs">{fmtTime(a.startedAt)}</td>
                  <td className="td mono text-xs text-danger">{a.error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}